/**
 * Clinic portal single consent detail hook.
 * API calls go through @/services/main/consent.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import useAppToast from '@/hooks/useAppToast';
import { getConsentById } from '@/services/main/consent';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

interface UseConsentDetailParams {
  consentId?: string;
  patientId?: string;
  enabled?: boolean;
}

export const useConsentDetail = ({
  consentId,
  patientId,
  enabled = true,
}: UseConsentDetailParams) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['consents', 'detail', tenant, clinicNumber, patientId, consentId, serviceType],
    queryFn: async () => {
      if (!consentId || !patientId) return null;
      const res = await getConsentById(tenant, clinicNumber, patientId, consentId, serviceType);
      const consent = res?.data?.consent;
      if (!consent || typeof consent !== 'object') return null;
      return {
        ...consent,
        has_visits_in_treatment_period: (res?.data as Record<string, unknown>)?.has_visits_in_treatment_period ?? false,
      };
    },
    enabled:
      enabled && !!tenant && !!clinicNumber && !!patientId && !!consentId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
};

export default useConsentDetail;
